import axios, { AxiosRequestConfig, AxiosError } from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.clicklaw.tech/api/v1';
// Sample local dev: http://localhost:8000/api/v1
// Storage keys (consistent with existing clicklaw.* pattern)
export const AUTH_KEYS = {
  accessToken: 'clicklaw.accessToken',
  refreshToken: 'clicklaw.refreshToken',
  user: 'clicklaw.user',
} as const;

// Create axios instance (Accept must match backend / generated client: application/vnd.api.v1+json)
export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/vnd.api.v1+json',
  },
});

// Request interceptor - attach token; allow FormData to set Content-Type (multipart)
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_KEYS.accessToken);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // So backend receives multipart file: do not send application/json when body is FormData
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 and refresh token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Subscription required — redirect to paywall (skip if already on subscription pages)
    if (error.response?.status === 402) {
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/dashboard/subscription')) {
        window.location.href = '/dashboard/subscription/required';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentToken = localStorage.getItem(AUTH_KEYS.accessToken);

      if (!currentToken) {
        // No token - clear auth and redirect to login
        localStorage.removeItem(AUTH_KEYS.accessToken);
        localStorage.removeItem(AUTH_KEYS.refreshToken);
        localStorage.removeItem(AUTH_KEYS.user);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Backend expects Authorization: Bearer <jwt> and returns { data: { token } }
        const response = await axios.post(
          `${BASE_URL}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${currentToken}` } }
        );

        const newToken = response.data?.data?.token;
        if (!newToken) {
          throw new Error('Invalid refresh response');
        }

        localStorage.setItem(AUTH_KEYS.accessToken, newToken);

        processQueue(null, newToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        localStorage.removeItem(AUTH_KEYS.accessToken);
        localStorage.removeItem(AUTH_KEYS.refreshToken);
        localStorage.removeItem(AUTH_KEYS.user);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Custom instance function for orval mutator
export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = axiosInstance({
    ...config,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-expect-error - Add cancel method for React Query
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};

export default customInstance;
